import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { PlusCircle } from "@phosphor-icons/react";

const METHODS = [
    { v: "cash", l: "Cash" },
    { v: "zelle", l: "Zelle" },
    { v: "venmo", l: "Venmo" },
];

const getHandle = (method, venue) => {
    if (method === "zelle") return venue?.zelle || "";
    if (method === "venmo") return venue?.venmo || "";
    return "";
};

export default function BuyInDialog({ seatedPlayers, venue, onSubmit }) {
    const [open, setOpen] = useState(false);
    const [playerId, setPlayerId] = useState("");
    const [amount, setAmount] = useState("");
    const [method, setMethod] = useState("cash");
    const [reference, setReference] = useState("");

    const reset = () => {
        setPlayerId(""); setAmount(""); setMethod("cash"); setReference("");
    };

    const confirm = async () => {
        if (!playerId || !amount) { toast.error("Select player and amount"); return; }
        await onSubmit({
            player_id: playerId,
            amount: parseFloat(amount),
            method,
            reference: reference || null,
        });
        setOpen(false);
        reset();
    };

    const handle = getHandle(method, venue);

    return (
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
            <DialogTrigger asChild>
                <Button data-testid="btn-open-buyin" className="btn-gold h-10">
                    <PlusCircle size={16} className="mr-1.5" /> Buy-in
                </Button>
            </DialogTrigger>
            <DialogContent className="bg-card">
                <DialogHeader><DialogTitle className="font-serif text-2xl">Record buy-in</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="label-mini">Player</Label>
                        <Select value={playerId} onValueChange={setPlayerId}>
                            <SelectTrigger data-testid="select-buyin-player"><SelectValue placeholder="Pick" /></SelectTrigger>
                            <SelectContent>
                                {seatedPlayers.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="label-mini">Amount ($)</Label>
                        <Input data-testid="input-buyin-amount" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="100.00" />
                    </div>
                    <div>
                        <Label className="label-mini">Payment method</Label>
                        <div className="grid grid-cols-3 gap-2 mt-1">
                            {METHODS.map(o => (
                                <button
                                    key={o.v}
                                    data-testid={`btn-method-${o.v}`}
                                    type="button"
                                    onClick={() => setMethod(o.v)}
                                    className={`h-10 rounded-md text-sm transition-colors border ${
                                        method === o.v
                                            ? "border-gold bg-gold/15 text-gold"
                                            : "border-border text-muted-foreground hover:text-foreground"
                                    }`}
                                >
                                    {o.l}
                                </button>
                            ))}
                        </div>
                    </div>
                    {method !== "cash" && (
                        <div className="rounded-md border border-gold/30 bg-secondary/40 p-3 text-sm space-y-1" data-testid="payment-handle-box">
                            <div className="label-mini">Ask player to transfer to</div>
                            <div className="font-mono-plex text-base">{handle || `(set VENUE_${method.toUpperCase()})`}</div>
                            <div>
                                <Label className="label-mini">Confirmation / sender handle</Label>
                                <Input
                                    data-testid="input-buyin-ref"
                                    value={reference}
                                    onChange={(e) => setReference(e.target.value)}
                                    placeholder={method === "zelle" ? "player@email or last 4 of confirmation" : "@player-venmo"}
                                />
                            </div>
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button data-testid="btn-confirm-buyin" onClick={confirm} className="btn-gold">Record</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
